import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { PlotLine } from '../types';

interface ReorderTimelinesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timelines: PlotLine[];
  onReorderTimelines: (timelineIds: number[]) => void;
  isSaving: boolean;
}

interface TimelineItemProps {
  timeline: PlotLine;
  isDragging?: boolean;
}

const TimelineItem = React.memo(React.forwardRef<HTMLDivElement, TimelineItemProps & React.HTMLAttributes<HTMLDivElement>>(
  ({ timeline, isDragging, style, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        style={style}
        className={`relative rounded-xl border-border/40 shadow-sm bg-card transition-colors ${isDragging ? 'opacity-30' : 'hover:bg-muted/30'}`}
        {...props}
      >
        <CardContent className="p-3 flex items-center">
          <div className="mr-2 cursor-move hover:text-primary transition-colors">
            <GripVertical className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium select-none">{timeline.name}</div>
            {timeline.description && (
              <div className="text-xs text-muted-foreground mt-1 select-none">{timeline.description}</div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
));

TimelineItem.displayName = 'TimelineItem';

const SortableTimelineItem: React.FC<{ timeline: PlotLine }> = ({ timeline }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: timeline.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TimelineItem
      ref={setNodeRef}
      style={style}
      timeline={timeline}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    />
  );
};

export const ReorderTimelinesDialog: React.FC<ReorderTimelinesDialogProps> = ({
  open,
  onOpenChange,
  timelines,
  onReorderTimelines,
  isSaving
}) => {
  const [orderedTimelines, setOrderedTimelines] = useState<PlotLine[]>([]);

  // Update local ordered timelines whenever the timelines prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setOrderedTimelines([...timelines]);
    }
  }, [timelines, open]);

  // Configure sensors for drag events
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      setOrderedTimelines((items) => {
        const oldIndex = items.findIndex(item => item.id.toString() === active.id);
        const newIndex = items.findIndex(item => item.id.toString() === over.id);

        // Get ordered list after move
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update the _order property explicitly for each timeline
        return newItems.map((timeline, index) => ({
          ...timeline,
          _order: index // Explicitly set the _order field based on new position
        }));
      });
    }
  };

  const handleSaveOrder = () => {
    console.log('Saving timeline order:', orderedTimelines.map(tl => ({ id: tl.id, name: tl.name, _order: tl._order })));

    // Pass the ordered timeline IDs to the parent component
    onReorderTimelines(orderedTimelines.map(timeline => timeline.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-border/40 bg-muted/5">
          <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">Reorder Timelines</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Drag and drop timelines to reorder them. The order will be reflected in the interface.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[60vh] overflow-y-auto bg-background/50">
          {timelines.length === 0 ? (
            <div className="text-center p-4 text-muted-foreground">
              No timelines yet. Add timelines to the story first.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedTimelines.map(timeline => timeline.id.toString())}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {orderedTimelines.map((timeline) => (
                    <SortableTimelineItem key={timeline.id} timeline={timeline} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl hover:bg-muted/50">
            Cancel
          </Button>
          <Button
            onClick={handleSaveOrder}
            disabled={isSaving || timelines.length === 0}
            className="rounded-xl shadow-sm font-medium"
          >
            {isSaving ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Saving...
              </>
            ) : (
              'Save Order'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};